import { useCallback, useEffect, useState } from 'react';
import { Button, Callout, Icon, Spinner, Tab, TabGroup } from '@humanforce/mango-ui-react';
import { useSearchParams } from 'react-router';
import { getPayRunDetail, getPopulationStatus, getTransactionIssues } from './payRunPopulationHttpClient';
import {
  getPayRunCalculation,
  PayRunRequestError,
  retryPayRunCalculation,
  startPayRunCalculation,
  transitionPayRun,
  updatePayRun,
} from './payRunsHttpClient';
import type { PayRunDetailDto, PayRunFieldError } from './payRunsApiTypes';
import type {
  PayRunPopulationRefreshAccepted,
  PayRunPopulationStatus,
  PayRunTransactionIssuePage,
} from './payRunPopulationApiTypes';
import { PayRunDetailHeader } from './PayRunDetailHeader';
import { PayRunEmployeesTab } from './PayRunEmployeesTab';
import { PayRunExceptionsTab } from './PayRunExceptionsTab';
import { CancelPayRunModal, EditPayRunModal } from './PayRunActionDialogs';
import { PayRunDataLoadModal } from './PayRunDataLoadModal';

interface PayRunDetailScreenProps {
  payId: string;
}

interface ActionNotice {
  tone: 'positive' | 'critical';
  title: string;
  message: string;
  refreshRequired?: boolean;
}

type DetailTab = 'employees' | 'exceptions';

const readTab = (value: string | null): DetailTab =>
  value === 'exceptions' ? 'exceptions' : 'employees';

export const PayRunDetailScreen = ({ payId }: PayRunDetailScreenProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readTab(searchParams.get('tab'));
  const [payRun, setPayRun] = useState<PayRunDetailDto | null>(null);
  const [status, setStatus] = useState<PayRunPopulationStatus | null>(null);
  const [transactionIssues, setTransactionIssues] = useState<PayRunTransactionIssuePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<'edit' | 'cancel' | 'load' | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PayRunFieldError[]>([]);
  const [actionNotice, setActionNotice] = useState<ActionNotice | null>(null);
  const [displayLabel, setDisplayLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [populationReloadKey, setPopulationReloadKey] = useState(0);
  const [calculationBusy, setCalculationBusy] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const actionLocked = actionBusy || Boolean(actionNotice?.refreshRequired);

  const loadHeader = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [nextPayRun, nextStatus, nextTransactionIssues] = await Promise.all([
        getPayRunDetail(payId, signal),
        getPopulationStatus(payId, signal),
        getTransactionIssues(payId, { page: 1, pageSize: 1 }, signal),
      ]);
      setPayRun(nextPayRun);
      setStatus(nextStatus);
      setTransactionIssues(nextTransactionIssues);
    } catch (loadError) {
      if (signal?.aborted) return;
      setError(loadError instanceof Error ? loadError.message : 'Pay Run details could not be loaded.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [payId]);

  useEffect(() => {
    const controller = new AbortController();
    void loadHeader(controller.signal);
    return () => controller.abort();
  }, [loadHeader]);

  useEffect(() => {
    if (status?.loadStatus !== 'loading') return undefined;
    const timer = window.setInterval(() => {
      void getPopulationStatus(payId).then((nextStatus) => {
        setStatus(nextStatus);
        if (nextStatus.loadStatus !== 'loading') {
          void getPayRunDetail(payId).then(setPayRun).catch(() => undefined);
        }
      }).catch(() => undefined);
    }, 1500);
    return () => window.clearInterval(timer);
  }, [payId, status?.loadStatus]);

  useEffect(() => {
    const calculation = payRun?.calculation;
    if (!calculation?.attemptId || !['admitted', 'calculating'].includes(calculation.status)) return undefined;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      void getPayRunCalculation(payId, calculation.attemptId!, controller.signal)
        .then(async (nextCalculation) => {
          setCalculationError(null);
          if (!['admitted', 'calculating'].includes(nextCalculation.status)) {
            window.clearInterval(timer);
            const refreshed = await getPayRunDetail(payId, controller.signal);
            setPayRun(refreshed);
            setPopulationReloadKey((current) => current + 1);
          } else {
            setPayRun((current) => current ? { ...current, calculation: nextCalculation } : current);
          }
        })
        .catch(() => setCalculationError('Calculation status could not be refreshed. Retry the status check.'));
    }, 1500);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [payId, payRun?.calculation?.attemptId, payRun?.calculation?.status]);

  if (loading) {
    return (
      <div
        className="mgo-cluster mgo-justify-center"
        aria-busy="true"
        aria-label="Loading Pay Run details"
        style={{ minHeight: '15rem', padding: 'var(--mgo-space-60)' }}
      >
        <span style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner /></span>
      </div>
    );
  }

  if (error || !payRun) {
    return (
      <div className="mgo-stack mgo-gap-l" style={{ padding: 'var(--mgo-space-60)' }}>
        <Button label="Back to Pay Runs" variant="tertiary" icon="back" href="/payroll/pay-runs" />
        <Callout
          tone="critical"
          title="Pay Run details unavailable"
          action={<Button label="Retry" size="small" variant="secondary" onClick={() => void loadHeader()} />}
        >
          {error}
        </Callout>
      </div>
    );
  }

  const changeTab = (value: string) => {
    const next = readTab(value);
    setSearchParams(next === 'employees' ? {} : { tab: next }, { replace: true });
  };

  const refreshAccepted = (accepted: PayRunPopulationRefreshAccepted) => {
    setStatus((current) => ({
      ...(current ?? {
        isActive: false,
        blockingIssueCount: 0,
        warningIssueCount: 0,
        reviewRequiredIssueCount: 0,
      }),
      populationRevisionId: accepted.populationRevisionId,
      revisionNumber: accepted.revisionNumber,
      loadStatus: accepted.loadStatus,
    }));
  };

  const applyTransition = async (verb: 'make_payroll_active' | 'revert_to_trial') => {
    if (transitioning || actionLocked) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      setPayRun(await transitionPayRun(payId, verb));
    } catch (transitionFailure) {
      setTransitionError(transitionFailure instanceof Error
        ? transitionFailure.message
        : 'The Pay Run could not be transitioned.');
    } finally {
      setTransitioning(false);
    }
  };

  const reloadPayRun = async () => {
    const refreshed = await getPayRunDetail(payId);
    setPayRun(refreshed);
    return refreshed;
  };

  const reloadAfterDataLoad = async () => {
    try {
      const [nextPayRun, nextStatus, nextTransactionIssues] = await Promise.all([
        getPayRunDetail(payId),
        getPopulationStatus(payId),
        getTransactionIssues(payId, { page: 1, pageSize: 1 }),
      ]);
      setPayRun(nextPayRun);
      setStatus(nextStatus);
      setTransactionIssues(nextTransactionIssues);
      setPopulationReloadKey((current) => current + 1);
    } catch {
      setActionNotice({
        tone: 'critical',
        title: 'Refresh required',
        message: 'The Pay Run data was loaded, but current details could not be refreshed.',
        refreshRequired: true,
      });
    }
  };

  const restoreActionFocus = () => {
    window.setTimeout(() => {
      const trigger = document.querySelector<HTMLButtonElement>('[data-testid="pay-run-actions"] button');
      if (trigger) trigger.focus();
      else document.querySelector<HTMLElement>('[data-testid="pay-run-action-result"]')?.focus();
    }, 350);
  };

  const closeDialog = () => {
    if (actionBusy) return;
    setActiveDialog(null);
    setActionError(null);
    setFieldErrors([]);
    restoreActionFocus();
  };

  const openEdit = () => {
    if (transitioning || actionLocked) return;
    setDisplayLabel(payRun.displayLabel ?? '');
    setNotes(payRun.notes ?? '');
    setActionError(null);
    setFieldErrors([]);
    setActionNotice(null);
    setActiveDialog('edit');
  };

  const openCancel = () => {
    if (transitioning || actionLocked) return;
    setActionError(null);
    setFieldErrors([]);
    setActionNotice(null);
    setActiveDialog('cancel');
  };

  const safeActionError = (failure: unknown) => {
    if (!(failure instanceof PayRunRequestError)) return 'Please try again. If the problem continues, contact support.';
    if (failure.status === 401) return 'Your session has expired. Sign in again before retrying.';
    if (failure.status === 403) return 'You no longer have permission to perform this action.';
    if (failure.status === 409) return 'The Pay Run changed before this action completed. Review its current state before retrying.';
    if (failure.status >= 500) return 'The Pay Run service is temporarily unavailable. Try again when it is available.';
    return failure.fieldErrors.length > 0 ? 'Review the highlighted fields and try again.' : 'The request could not be completed.';
  };

  const saveEdit = async () => {
    if (actionBusy || !payRun.meta.actions.includes('edit') || displayLabel.length > 200 || notes.length > 500) return;
    setActionBusy(true);
    setActionNotice(null);
    setActionError(null);
    setFieldErrors([]);
    try {
      await updatePayRun(payId, {
        displayLabel: displayLabel.trim() || null,
        notes: notes.trim() || null,
      });
      try {
        await reloadPayRun();
        setActionNotice({ tone: 'positive', title: 'Pay Run updated', message: 'The current Pay Run details are displayed.' });
      } catch {
        setActionNotice({
          tone: 'critical',
          title: 'Pay Run updated; refresh required',
          message: 'The Pay Run was updated, but current details could not be refreshed.',
          refreshRequired: true,
        });
      }
      setActiveDialog(null);
      restoreActionFocus();
    } catch (failure) {
      if (failure instanceof PayRunRequestError) {
        setFieldErrors(failure.fieldErrors);
        if (failure.status === 409) {
          try { await reloadPayRun(); } catch { /* retain the actionable write error */ }
        }
      }
      setActionError(safeActionError(failure));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (actionBusy || !payRun.meta.actions.includes('cancel')) return;
    setActionBusy(true);
    setActionNotice(null);
    setActionError(null);
    try {
      await transitionPayRun(payId, 'cancel');
      try {
        await reloadPayRun();
        setActionNotice({ tone: 'positive', title: 'Pay Run cancelled', message: 'The retained Pay Run details are displayed.' });
      } catch {
        setActionNotice({
          tone: 'critical',
          title: 'Pay Run cancelled; refresh required',
          message: 'The Pay Run was cancelled, but current details could not be refreshed.',
          refreshRequired: true,
        });
      }
      setActiveDialog(null);
      restoreActionFocus();
    } catch (failure) {
      if (failure instanceof PayRunRequestError && failure.status === 409) {
        try { await reloadPayRun(); } catch { /* retain the actionable write error */ }
      }
      setActionError(safeActionError(failure));
    } finally {
      setActionBusy(false);
    }
  };

  const retryActionRefresh = async () => {
    if (actionBusy || transitioning) return;
    setActionBusy(true);
    try {
      await reloadPayRun();
      setActionNotice({ tone: 'positive', title: 'Pay Run details refreshed', message: 'The current server state is displayed.' });
    } catch {
      setActionNotice((current) => current ?? {
        tone: 'critical',
        title: 'Refresh required',
        message: 'Current Pay Run details could not be refreshed.',
        refreshRequired: true,
      });
    } finally {
      setActionBusy(false);
      restoreActionFocus();
    }
  };

  const calculate = async () => {
    if (calculationBusy) return;
    setCalculationBusy(true);
    setCalculationError(null);
    try {
      const current = payRun.calculation;
      const next = current?.attemptId && ['failed', 'review_required'].includes(current.status)
        ? await retryPayRunCalculation(payId, current.attemptId)
        : await startPayRunCalculation(payId, crypto.randomUUID());
      setPayRun((value) => value ? { ...value, calculation: next } : value);
    } catch (failure) {
      setCalculationError(failure instanceof Error
        ? failure.message
        : 'The calculation could not be started.');
    } finally {
      setCalculationBusy(false);
    }
  };

  const refreshCalculation = async () => {
    const attemptId = payRun.calculation?.attemptId;
    if (!attemptId || calculationBusy) return;
    setCalculationBusy(true);
    setCalculationError(null);
    try {
      const calculation = await getPayRunCalculation(payId, attemptId);
      setPayRun((value) => value ? { ...value, calculation } : value);
    } catch {
      setCalculationError('Calculation status could not be refreshed. Retry the status check.');
    } finally {
      setCalculationBusy(false);
    }
  };

  return (
    <div className="mgo-stack mgo-gap-l" style={{ padding: 'var(--mgo-space-60)' }}>
      <a
        href="/payroll/pay-runs"
        className="mgo-cluster mgo-gap-xs mgo-text-s"
        style={{ color: 'var(--mgo-color-fg-link)', textDecoration: 'none', alignSelf: 'flex-start' }}
      >
        <Icon name="back" /> Pay Runs
      </a>
      {transitionError && <Callout tone="critical" title="Pay Run transition failed">{transitionError}</Callout>}
      {actionNotice && (
        <div
          role={actionNotice.tone === 'critical' ? 'alert' : 'status'}
          aria-live={actionNotice.tone === 'critical' ? 'assertive' : 'polite'}
          tabIndex={-1}
          data-testid="pay-run-action-result"
        >
          <Callout
            tone={actionNotice.tone}
            title={actionNotice.title}
            action={actionNotice.refreshRequired
              ? <Button label="Retry refresh" size="small" variant="secondary" loading={actionBusy} onClick={() => void retryActionRefresh()} />
              : undefined}
          >
            {actionNotice.message}
          </Callout>
        </div>
      )}
      <PayRunDetailHeader
        payRun={payRun}
        populationStatus={status}
        transactionIssues={transactionIssues}
        transitioning={transitioning}
        actionBusy={actionLocked}
        calculationBusy={calculationBusy}
        calculationError={calculationError}
        onCalculate={() => void calculate()}
        onRefreshCalculation={() => void refreshCalculation()}
        onTransition={(verb) => void applyTransition(verb)}
        onLoadPopulation={() => setActiveDialog('load')}
        onEdit={openEdit}
        onCancel={openCancel}
      />

      <EditPayRunModal
        payRun={payRun}
        visible={activeDialog === 'edit'}
        displayLabel={displayLabel}
        notes={notes}
        busy={actionBusy}
        actionAllowed={payRun.meta.actions.includes('edit')}
        error={actionError}
        fieldErrors={fieldErrors}
        onDisplayLabelChange={setDisplayLabel}
        onNotesChange={setNotes}
        onSave={() => void saveEdit()}
        onClose={closeDialog}
      />
      <CancelPayRunModal
        payRun={payRun}
        visible={activeDialog === 'cancel'}
        busy={actionBusy}
        actionAllowed={payRun.meta.actions.includes('cancel')}
        error={actionError}
        onConfirm={() => void confirmCancel()}
        onClose={closeDialog}
      />
      <PayRunDataLoadModal
        payId={payId}
        visible={activeDialog === 'load'}
        onHide={() => {
          setActiveDialog(null);
          void reloadAfterDataLoad();
        }}
        onAccepted={refreshAccepted}
      />

      <TabGroup value={activeTab} onValueChange={changeTab}>
        <Tab value="employees" label="Employees" leftIcon={<Icon name="person" />}>
          <PayRunEmployeesTab
            key={`employees-${populationReloadKey}`}
            payId={payId}
            workflowStatus={payRun.workflowStatus}
            calculationStatus={payRun.calculation?.status ?? 'not_ready'}
            populationStatus={status?.loadStatus}
            canRefreshPopulation={payRun.meta.actions.includes('refresh_population')}
            onRefreshAccepted={refreshAccepted}
          />
        </Tab>
        <Tab
          value="exceptions"
          label="Exceptions"
          leftIcon={<Icon name="status-caution" />}
          badge={(status?.blockingIssueCount ?? 0) + (status?.warningIssueCount ?? 0)
            + (status?.reviewRequiredIssueCount ?? 0) + (transactionIssues?.blocking ?? 0)
            + (transactionIssues?.reviewRequired ?? 0)}
          badgeTone={(status?.blockingIssueCount ?? 0) + (transactionIssues?.blocking ?? 0) > 0
            ? 'critical'
            : 'caution'}
        >
          <PayRunExceptionsTab
            key={`exceptions-${populationReloadKey}`}
            payId={payId}
            populationStatus={status?.loadStatus}
          />
        </Tab>
      </TabGroup>
    </div>
  );
};
